import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHeader } from "../components/PageHeader";

function buildAbsoluteBookingUrl(path) {
  const normalizedPath = typeof path === "string" ? path.trim() : "";
  if (!normalizedPath) {
    return "";
  }

  const origin = typeof window !== "undefined" ? window.location?.origin?.replace(/\/$/, "") : "";
  return origin ? `${origin}${normalizedPath}` : normalizedPath;
}

function getLoadErrorMessage(error) {
  if (error?.response?.status === 403) {
    return "Accesso riservato al titolare.";
  }
  return "Impossibile caricare il profilo attività.";
}

function getSaveErrorMessage(error) {
  const detail = error?.response?.data?.detail;
  if (error?.response?.status === 403) {
    return "Accesso riservato al titolare.";
  }
  if (detail === "Business slug already exists") {
    return "Questo slug è già in uso. Scegline un altro.";
  }
  if (detail === "Business label is required") {
    return "Il nome pubblico dell'attività è obbligatorio.";
  }
  if (detail === "Business slug is invalid" || error?.response?.status === 422) {
    return "Lo slug pubblico non è valido.";
  }
  return "Impossibile aggiornare il profilo attività.";
}

export function BusinessProfilePage() {
  const [form, setForm] = useState({ business_label: "", business_slug: "" });
  const [savedProfile, setSavedProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState("");
  const [error, setError] = useState("");

  const loadBusinessProfile = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get("/business-profile");
      setSavedProfile(response.data);
      setForm({
        business_label: response.data?.business_label || "",
        business_slug: response.data?.business_slug || "",
      });
    } catch (loadError) {
      const message = getLoadErrorMessage(loadError);
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBusinessProfile();
  }, [loadBusinessProfile]);

  const publicBookingPath = useMemo(() => {
    const slug = (form.business_slug || "").trim();
    return slug ? `/book/${slug}` : "";
  }, [form.business_slug]);

  const currentBookingUrl = useMemo(() => buildAbsoluteBookingUrl(publicBookingPath), [publicBookingPath]);
  const savedBookingUrl = useMemo(
    () => buildAbsoluteBookingUrl(savedProfile?.public_booking_path),
    [savedProfile?.public_booking_path]
  );
  const canShare = typeof navigator !== "undefined" && typeof navigator.share === "function";
  const canCopy = typeof navigator !== "undefined" && typeof navigator.clipboard?.writeText === "function";

  const handleChange = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    if (copyFeedback) {
      setCopyFeedback("");
    }
  };

  const handleCopyLink = async () => {
    if (!currentBookingUrl) {
      return;
    }
    try {
      await navigator.clipboard.writeText(currentBookingUrl);
      setCopyFeedback("Link copiato.");
      toast.success("Link copiato.");
      return;
    } catch (copyError) {
      setCopyFeedback("Copia non riuscita. Usa il link manuale qui sotto.");
      toast.error("Copia non riuscita. Usa il link manuale.");
    }
  };

  const handleShareLink = async () => {
    if (!currentBookingUrl || !canShare) {
      return;
    }
    try {
      await navigator.share({
        title: "Prenotazione FullBook",
        text: "Prenota il tuo appuntamento online.",
        url: currentBookingUrl,
      });
      setCopyFeedback("Link condiviso.");
    } catch (shareError) {
      if (shareError?.name === "AbortError") {
        setCopyFeedback("Condivisione annullata.");
        return;
      }
      setCopyFeedback("Condivisione non riuscita. Usa il link manuale qui sotto.");
      toast.error("Condivisione non riuscita. Usa il link manuale.");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const response = await api.patch("/business-profile", form);
      setSavedProfile(response.data);
      setForm({
        business_label: response.data?.business_label || "",
        business_slug: response.data?.business_slug || "",
      });
      setCopyFeedback("");
      toast.success("Profilo attività aggiornato correttamente.");
    } catch (saveError) {
      toast.error(getSaveErrorMessage(saveError));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="business-profile-page">
      <PageHeader
        title="Profilo attività"
        subtitle="Gestisci il nome pubblico e il link di prenotazione che i clienti useranno online."
        titleTestId="business-profile-title"
        subtitleTestId="business-profile-subtitle"
      />

      <section className="grid gap-4 md:grid-cols-3" data-testid="business-profile-summary">
        <article className="rounded-sm border border-slate-200 bg-white p-4">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Nome pubblico attività</p>
          <p className="mt-2 text-sm font-medium text-slate-900">
            {savedProfile?.business_label || "Non disponibile"}
          </p>
        </article>
        <article className="rounded-sm border border-slate-200 bg-white p-4">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Slug pubblico</p>
          <p className="mt-2 text-sm font-medium text-slate-900">
            {savedProfile?.business_slug || "Non disponibile"}
          </p>
        </article>
        <article className="rounded-sm border border-slate-200 bg-white p-4">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Link pubblico di prenotazione</p>
          <p className="mt-2 break-all text-sm font-medium text-slate-900">
            {savedBookingUrl || "Non disponibile"}
          </p>
        </article>
      </section>

      {loading && (
        <div className="rounded-sm border border-slate-200 bg-white p-6 text-sm text-slate-500" data-testid="business-profile-loading">
          Caricamento profilo attività...
        </div>
      )}

      {!loading && error && (
        <div className="rounded-sm border border-red-200 bg-white p-6 text-sm text-red-600" data-testid="business-profile-error">
          {error}
        </div>
      )}

      {!loading && !error && (
        <form
          className="space-y-5 rounded-sm border border-slate-200 bg-white p-4 md:p-5"
          onSubmit={handleSubmit}
          data-testid="business-profile-form"
        >
          <div className="grid gap-4 md:grid-cols-2">
            <label className="text-sm text-slate-700">
              <span className="mb-1 block font-medium">Nome pubblico attività</span>
              <input
                type="text"
                value={form.business_label}
                onChange={(event) => handleChange("business_label", event.target.value)}
                className="w-full rounded-sm border border-slate-300 px-3 py-2 text-sm"
                placeholder="Es. Studio Rossi"
                data-testid="business-profile-label-input"
              />
            </label>
            <label className="text-sm text-slate-700">
              <span className="mb-1 block font-medium">Slug pubblico</span>
              <input
                type="text"
                value={form.business_slug}
                onChange={(event) => handleChange("business_slug", event.target.value)}
                className="w-full rounded-sm border border-slate-300 px-3 py-2 text-sm"
                placeholder="es-studio-rossi"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                data-testid="business-profile-slug-input"
              />
            </label>
          </div>

          <div className="rounded-sm border border-slate-200 bg-slate-50 p-4" data-testid="business-profile-link-card">
            <div className="space-y-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900">Link pubblico di prenotazione</p>
                <input
                  type="url"
                  readOnly
                  value={currentBookingUrl}
                  onFocus={(event) => event.currentTarget.select()}
                  aria-label="Link pubblico selezionabile"
                  className="mt-2 h-11 w-full min-w-0 rounded-sm border border-slate-300 bg-white px-3 text-sm text-slate-700"
                  data-testid="business-profile-public-link"
                />
                <p className="mt-2 text-xs text-slate-500" data-testid="business-profile-manual-copy-hint">
                  Tieni premuto sul link per copiarlo.
                </p>
                {!canShare && !canCopy && (
                  <p className="mt-1 text-xs text-slate-500" data-testid="business-profile-manual-only">
                    Condivisione e copia automatica non sono disponibili in questo ambiente.
                  </p>
                )}
                {copyFeedback && (
                  <p className="mt-2 text-xs text-slate-500" data-testid="business-profile-copy-feedback">
                    {copyFeedback}
                  </p>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {canShare && <button type="button" onClick={handleShareLink} disabled={!currentBookingUrl} className="min-h-11 rounded-sm bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:bg-slate-300" data-testid="business-profile-share-link-button">Condividi link</button>}
                {canCopy && <button type="button" onClick={handleCopyLink} disabled={!currentBookingUrl} className="min-h-11 rounded-sm border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 disabled:text-slate-400" data-testid="business-profile-copy-link-button">Copia link</button>}
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="rounded-sm bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:bg-slate-300"
              data-testid="business-profile-save-button"
            >
              {saving ? "Salvataggio..." : "Salva profilo"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
